import { useState } from 'react'

import { CardCheckbox, type CardCheckboxSize } from '@/components/ui/CardCheckbox'
import { CopyButton } from '@/components/ui/CopyButton'
import { ChatCardCheckbox } from '../../../../components/Chat/ChatCardCheckbox'
import { Cap, Demo, DsSection } from '../primitives'

const ELD_PROVIDERS = [
  { label: 'Motive', value: 'motive' },
  { label: 'Samsara', value: 'samsara' },
  { label: 'Geotab', value: 'geotab' },
  { label: 'Verizon Connect', value: 'verizon_connect' },
]

const MARITAL_STATUS_OPTIONS = [
  { label: 'Single', value: 'SINGLE' },
  { label: 'Married', value: 'MARRIED' },
  { label: 'Divorced', value: 'DIVORCED' },
  { label: 'Widowed', value: 'WIDOWED' },
]

const TRAVEL_RANGES = [
  { label: 'Local (0–50 miles)', value: 'LOCAL' },
  { label: 'Long Haul (300+ miles)', value: 'LONG_HAUL' },
]

function PropName({ value, onDark }: { value: string; onDark?: boolean }) {
  return (
    <div
      className={`flex items-center gap-1 font-mono text-[10px] ${onDark ? 'text-ink-300' : 'text-ink-400'}`}
    >
      <span>{value}</span>
      <CopyButton
        value={value}
        className={
          onDark
            ? 'size-4 bg-transparent text-white/70 hover:bg-transparent hover:text-white'
            : 'text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent'
        }
      />
    </div>
  )
}

/** One controlled column of cards, so each variant demo is independently interactive. */
function CardColumn({
  value,
  size,
  onDark,
  disabled,
  readOnly,
}: {
  value: string
  size?: CardCheckboxSize
  onDark?: boolean
  disabled?: boolean
  readOnly?: boolean
}) {
  const [selected, setSelected] = useState<string[]>(['LOCAL'])
  return (
    <div className="flex w-full max-w-70 flex-col items-start gap-2">
      <div className="flex w-full flex-col gap-2">
        {TRAVEL_RANGES.map((option) => (
          <CardCheckbox
            key={option.value}
            size={size}
            onDark={onDark}
            disabled={disabled}
            readOnly={readOnly}
            isChecked={selected.includes(option.value)}
            onChange={(checked) =>
              setSelected(checked ? [...selected, option.value] : selected.filter((v) => v !== option.value))
            }
          >
            {option.label}
          </CardCheckbox>
        ))}
      </div>
      <PropName value={value} onDark={onDark} />
    </div>
  )
}

export function CardCheckboxSection() {
  return (
    <DsSection id="card-checkbox" title="Card checkbox">
      <Demo label="Sizes" className="flex flex-wrap items-start gap-6">
        <CardColumn value='size="default"' size="default" />
        <CardColumn value='size="sm"' size="sm" />
      </Demo>
      <Demo label="States" className="flex flex-wrap items-start gap-6">
        <CardColumn value="disabled" disabled />
        <CardColumn value="readOnly" readOnly />
      </Demo>
      <Demo label="On dark" dark className="flex flex-wrap items-start gap-6">
        <CardColumn value="onDark" onDark />
        <CardColumn value='onDark size="sm"' onDark size="sm" />
      </Demo>
      <Demo label="ChatCardCheckbox — multi-select, 2 columns, with “Other”">
        <ChatCardCheckbox options={ELD_PROVIDERS} columns={2} allowOther handleSubmit={() => undefined} />
      </Demo>
      <Demo label="ChatCardCheckbox — single-select, 1 column">
        <ChatCardCheckbox
          options={MARITAL_STATUS_OPTIONS}
          columns={1}
          singleSelect
          handleSubmit={() => undefined}
        />
      </Demo>
      <Demo label="ChatCardCheckbox — readonly (already answered)">
        <Cap className="mb-2">Disabled, non-interactive — shown for a past chat answer</Cap>
        <ChatCardCheckbox
          options={ELD_PROVIDERS}
          columns={2}
          defaultSelected={['samsara']}
          readonly
          handleSubmit={() => undefined}
        />
      </Demo>
    </DsSection>
  )
}
